import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { Slide, Slides } from "pages/media/types/";

export const useSlides = (path?: string) => {
  const queryClient = useQueryClient();

  const query = useQuery<Slides>({
    queryKey: ["slides", path],
    queryFn: async () => {
      const response = await fetch(path!); // we know path is not undefined because of the enabled condition
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      return response.json();
    },
    enabled: !!path,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const updateSlide = useCallback(
    (uuid: string, updates: Slide) => {
      queryClient.setQueryData<Slides>(["slides", path], (old) =>
        old
          ? {
              slides: old.slides.map((s) => (s.uuid === uuid ? updates : s)),
            }
          : old
      );
    },
    [queryClient, path]
  );

  const addSlide = useCallback(
    (slide: Slide) => {
      queryClient.setQueryData<Slides>(["slides", path], (old) =>
        old ? { slides: [...old.slides, slide] } : { slides: [slide] }
      );
    },
    [queryClient, path]
  );

  const setSlide = useCallback(
    (uuid: string, slide: Slide) => {
      queryClient.setQueryData<Slides>(["slides", path], (old) =>
        old
          ? { slides: old.slides.map((s) => (s.uuid === uuid ? slide : s)) }
          : old
      );
    },
    [queryClient, path]
  );

  const insertSlideBefore = useCallback(
    (slide: Slide, beforeUuid: string | null) => {
      queryClient.setQueryData<Slides>(["slides", path], (old) => {
        if (!old) return { slides: [slide] };
        if (beforeUuid === null) return { slides: [...old.slides, slide] };
        const index = old.slides.findIndex((s) => s.uuid === beforeUuid);
        if (index === -1) return { slides: [...old.slides, slide] };
        const next = [...old.slides];
        next.splice(index, 0, slide);
        return { slides: next };
      });
    },
    [queryClient, path]
  );

  const removeSlide = useCallback(
    (uuid: string) => {
      queryClient.setQueryData<Slides>(["slides", path], (old) =>
        old ? { slides: old.slides.filter((s) => s.uuid !== uuid) } : old
      );
    },
    [queryClient, path]
  );

  // Replace the whole slides array (used when reordering scenes).
  const setSlides = useCallback(
    (nextSlides: Slide[]) => {
      queryClient.setQueryData<Slides>(["slides", path], () => ({
        slides: nextSlides,
      }));
    },
    [queryClient, path]
  );

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    error: query.error,
    refetch: query.refetch,
    updateSlide,
    addSlide,
    setSlide,
    insertSlideBefore,
    removeSlide,
    setSlides,
  };
};
